import { useMemo } from "react";
import { fmtDate, fmtDateKey } from "@/lib/format";

/**
 * Pure data-transformation memos used by ScheduleGrid.
 * Extracted so the grid component focuses on layout + interaction.
 */
export function useGridIndexes({ rooms, tasks, crew, assignments, blockers }) {
  const roomById = useMemo(
    () => Object.fromEntries(rooms.map((r) => [r.id, r])),
    [rooms]
  );

  const taskById = useMemo(
    () => Object.fromEntries(tasks.map((t) => [t.id, t])),
    [tasks]
  );

  const crewById = useMemo(
    () => Object.fromEntries(crew.map((c) => [c.id, c])),
    [crew]
  );

  const subtasksByParent = useMemo(() => {
    const m = {};
    for (const t of tasks) {
      if (t.parent_task_id && !t.deleted_at) {
        (m[t.parent_task_id] ||= []).push(t);
      }
    }
    for (const k in m) {
      m[k].sort((a, b) => (a.order_within_room || 0) - (b.order_within_room || 0));
    }
    return m;
  }, [tasks]);

  const assignmentsByBlock = useMemo(() => {
    const m = {};
    for (const a of assignments) {
      (m[a.schedule_block_id] ||= []).push(a);
    }
    return m;
  }, [assignments]);

  const blockersByTask = useMemo(() => {
    const m = {};
    for (const b of blockers) {
      (m[b.task_id] ||= []).push(b);
    }
    return m;
  }, [blockers]);

  return { roomById, taskById, crewById, subtasksByParent, assignmentsByBlock, blockersByTask };
}

/**
 * Filter + sort blocks against active filters; respects search across title & notes.
 */
export function useFilteredBlocks({ blocks, taskById, assignmentsByBlock, filters }) {
  return useMemo(() => {
    return blocks
      .filter((b) => {
        const t = taskById[b.task_id];
        if (!t) return false;
        if (filters.rooms?.length && !filters.rooms.includes(b.room_id)) return false;
        if (filters.statuses?.length && !filters.statuses.includes(t.status)) return false;
        if (filters.priorities?.length && !filters.priorities.includes(t.priority)) return false;
        if (filters.crew?.length) {
          const ba = assignmentsByBlock[b.id] || [];
          if (!ba.some((a) => filters.crew.includes(a.crew_member_id))) return false;
        }
        if (filters.search) {
          const q = filters.search.toLowerCase();
          if (!t.title.toLowerCase().includes(q) && !(t.notes || "").toLowerCase().includes(q)) {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  }, [blocks, taskById, filters, assignmentsByBlock]);
}

/**
 * Build the rendered row list: date-band separators + block rows + (expanded) subtask rows.
 * After each day's last block, append a "newtask" row anchoring the +New task button.
 *
 * If `project.start_date` / `project.end_date` are present, EVERY day in that
 * window is shown — even when there are no blocks scheduled — so producers can
 * use the inline +NEW TASK row to plan an otherwise-empty day. Days outside
 * the window that already contain blocks (edge cases, e.g. early prep) are
 * also included.
 */
export function useGridRows({ filteredBlocks, subtasksByParent, expanded, project, groupMode = "date", rooms = [], floatingTasks = [], schedFilter = "all" }) {
  return useMemo(() => {
    if (groupMode === "room") {
      // Group by room_id first, then by date within room. Emits:
      //   { kind: "room", room_id }
      //   { kind: "date-sub", room_id, dateKey, label }   (date sub-band inside room)
      //   { kind: "block", block }
      //   { kind: "subtask", parent_block, subtask }
      //   { kind: "floating-divider", room_id }           (visual separator before unscheduled)
      //   { kind: "floating-task", task }                 (unscheduled tasks for this room)
      //   { kind: "newtask", room_id }                    (one per room, at end)
      const byRoom = new Map();
      for (const b of filteredBlocks) {
        const rid = b.room_id || null;
        if (!byRoom.has(rid)) byRoom.set(rid, []);
        byRoom.get(rid).push(b);
      }
      // Always include EVERY project room — even ones with zero tasks — so
      // producers can drop tasks into empty rooms via the inline + NEW TASK
      // row. Mirrors how date-view shows every day in the project window.
      for (const r of rooms) if (!byRoom.has(r.id)) byRoom.set(r.id, []);

      // Group floating tasks by their assigned room. Floating = no block.
      const floatingByRoom = new Map();
      for (const t of floatingTasks) {
        if (t.deleted_at || t.parent_task_id) continue;
        const rid = t.primary_room_id || null;
        if (!floatingByRoom.has(rid)) floatingByRoom.set(rid, []);
        floatingByRoom.get(rid).push(t);
      }
      // Sort floating tasks within a room by order_within_room then created_at.
      for (const arr of floatingByRoom.values()) {
        arr.sort((a, b) => {
          const oa = a.order_within_room ?? 0;
          const ob = b.order_within_room ?? 0;
          if (oa !== ob) return oa - ob;
          return String(a.created_at || "").localeCompare(String(b.created_at || ""));
        });
      }
      // Stable room order: follow the project's `rooms` array (which has sort_order),
      // then unassigned bucket last.
      const orderedRoomIds = [];
      for (const r of rooms) if (byRoom.has(r.id) || floatingByRoom.has(r.id)) orderedRoomIds.push(r.id);
      for (const rid of byRoom.keys()) {
        if (rid != null && !orderedRoomIds.includes(rid)) orderedRoomIds.push(rid);
      }
      if (byRoom.has(null) || floatingByRoom.has(null)) orderedRoomIds.push(null);

      const out = [];
      const roomById = Object.fromEntries(rooms.map((r) => [r.id, r]));
      const showScheduled = schedFilter !== "unscheduled";
      const showUnscheduled = schedFilter !== "scheduled";
      for (const rid of orderedRoomIds) {
        const room = rid ? roomById[rid] : null;
        const arr = (byRoom.get(rid) || []).slice().sort(
          (a, b) => new Date(a.start_at) - new Date(b.start_at),
        );
        const floats = floatingByRoom.get(rid) || [];
        const visibleScheduled = showScheduled ? arr.length : 0;
        const visibleFloats = showUnscheduled ? floats.length : 0;
        out.push({
          kind: "room", room_id: rid, room,
          taskCount: visibleScheduled + visibleFloats,
          scheduledCount: visibleScheduled, floatingCount: visibleFloats,
        });

        // Scheduled blocks (top), grouped by date sub-band.
        if (showScheduled) {
          let lastDateKey = null;
          for (const b of arr) {
            const dk = fmtDateKey(b.start_at);
            if (dk !== lastDateKey) {
              out.push({ kind: "date-sub", room_id: rid, dateKey: dk, label: fmtDate(b.start_at) });
              lastDateKey = dk;
            }
            out.push({ kind: "block", block: b });
            if (expanded.has(b.task_id)) {
              for (const s of subtasksByParent[b.task_id] || []) {
                out.push({ kind: "subtask", parent_block: b, subtask: s });
              }
            }
          }
        }

        // Floating (unscheduled) tasks for this room — at the BOTTOM of the
        // room group with a visual divider so they're clearly distinct.
        if (showUnscheduled && floats.length > 0) {
          out.push({ kind: "floating-divider", room_id: rid, count: floats.length });
          for (const t of floats) {
            out.push({ kind: "floating-task", task: t, room });
          }
        }
        out.push({ kind: "newtask", room_id: rid });
      }
      return out;
    }

    // ── Date grouping (original behavior) ────────────────────
    // 1) Group blocks by date-key.
    const blocksByDate = new Map();
    for (const b of filteredBlocks) {
      const dk = fmtDateKey(b.start_at);
      if (!blocksByDate.has(dk)) blocksByDate.set(dk, []);
      blocksByDate.get(dk).push(b);
    }

    // 2) Build the canonical date-key sequence: project window ∪ block dates.
    const dateKeys = new Set(blocksByDate.keys());
    if (project?.start_date && project?.end_date) {
      const start = new Date(`${project.start_date}T00:00:00`);
      const end = new Date(`${project.end_date}T00:00:00`);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && start <= end) {
        // Hard cap defensively — a 5-year project would otherwise nuke the grid.
        const maxDays = 365;
        let count = 0;
        for (const d = new Date(start); d <= end && count < maxDays; d.setDate(d.getDate() + 1)) {
          dateKeys.add(
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
          );
          count += 1;
        }
      }
    }
    const sortedDateKeys = [...dateKeys].sort();

    // 3) Emit rows in date order.
    const out = [];
    for (const dk of sortedDateKeys) {
      const dayBlocks = blocksByDate.get(dk) || [];
      // Label: use a block's start_at if any, else synthesise a midnight UTC iso so
      // fmtDate formats it consistently.
      const labelIso = dayBlocks[0]?.start_at || `${dk}T00:00:00Z`;
      out.push({ kind: "date", dateKey: dk, label: fmtDate(labelIso) });
      for (const b of dayBlocks) {
        out.push({ kind: "block", block: b });
        if (expanded.has(b.task_id)) {
          for (const s of subtasksByParent[b.task_id] || []) {
            out.push({ kind: "subtask", parent_block: b, subtask: s });
          }
        }
      }
      out.push({ kind: "newtask", dateKey: dk });
    }
    return out;
  }, [filteredBlocks, expanded, subtasksByParent, project, groupMode, rooms, floatingTasks, schedFilter]);
}

/**
 * For each crew member, sort all their assignments in time order. Used by the
 * grid to derive room-transfer arrows and "free Xm" gap labels per cell.
 */
export function useCrewTimelines({ blocks, assignments }) {
  return useMemo(() => {
    const blockById = Object.fromEntries(blocks.map((b) => [b.id, b]));
    const m = new Map();
    for (const a of assignments) {
      const b = blockById[a.schedule_block_id];
      if (!b || b.deleted_at) continue;
      const entry = {
        block_id: b.id,
        start_ms: new Date(b.start_at).getTime(),
        end_ms: new Date(b.end_at).getTime(),
        room_id: b.room_id,
      };
      if (!m.has(a.crew_member_id)) m.set(a.crew_member_id, []);
      m.get(a.crew_member_id).push(entry);
    }
    for (const arr of m.values()) arr.sort((x, y) => x.start_ms - y.start_ms);
    return m;
  }, [blocks, assignments]);
}

/**
 * For a given crew timeline and the current block's time window, return
 *   prev: the most recent past assignment (or null)
 *   current: the assignment overlapping this block (or null)
 *   next: the next future assignment (or null)
 */
export function crewContextFor(timeline, blockStartMs, blockEndMs) {
  if (!timeline) return { prev: null, current: null, next: null };
  let prev = null, current = null, next = null;
  for (const e of timeline) {
    if (e.end_ms <= blockStartMs) {
      prev = e;
    } else if (e.start_ms >= blockEndMs) {
      next = e;
      break;
    } else {
      current = e;
    }
  }
  return { prev, current, next };
}
